/**
 * Card Component
 * Container component with optional header and footer
 */

import { forwardRef } from 'react';
import type { CardProps } from '../types/components';
import { cn } from '../utils/cn';

const paddingStyles: Record<string, string> = {
  none: '',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
};

export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      className,
      header,
      footer,
      padding = 'md',
      hoverable = false,
      bordered = false,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          'bg-white/60 backdrop-blur-sm rounded-2xl shadow-sm shadow-gray-900/5 dark:bg-slate-900/60 dark:shadow-slate-950/20',
          bordered && '',
          hoverable && 'transition-shadow hover:shadow-md cursor-pointer',
          className
        )}
        {...props}
      >
        {header && (
          <div className="px-6 py-4 border-b border-gray-100/50 dark:border-slate-800/50">
            {typeof header === 'string' ? (
              <h3 className="text-lg font-semibold text-gray-900 dark:text-slate-100">{header}</h3>
            ) : (
              header
            )}
          </div>
        )}

        <div className={paddingStyles[padding]}>{children}</div>

        {footer && (
          <div className="px-6 py-4 border-t border-gray-100/50 bg-gray-50/50 rounded-b-2xl dark:border-slate-800/50 dark:bg-slate-800/50">
            {footer}
          </div>
        )}
      </div>
    );
  }
);

Card.displayName = 'Card';

export default Card;
